import { ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import Link from 'next/link';
import { signIn, useSession } from 'next-auth/react';
import { useTranslations } from 'next-intl';
import { Dispatch, SetStateAction, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import { TextField } from '@/components/Form/UI/TextField';
import LoginModal, {
  LoginModalActualForm,
} from '@/components/Modal/LoginModal';
import ValidateEmailModal from '@/components/Modal/ValidateEmailModal';
import Button from '@/components/UI/Button';
import { copyTextToClipboard } from '@/utils/copyToClipboard';
import { generateRandomPassword } from '@/utils/generateRandomPassword';
import { EMAIL_REGEX, PASSWORD_REGEX } from '@/utils/regex';

import PasswordCriteria from '../UI/PasswordCriteria';
import { AuthFormType, Register } from './Authform.type';
import OauthForm from './OauthForm';

interface RegisterFormProps {
  onSuccessCallback: () => void;
  setSelectedForm: Dispatch<SetStateAction<AuthFormType>>;
  aditionalContent?: React.ReactNode;
  allowGuestAccount?: boolean;
}

export default function RegisterForm({
  onSuccessCallback,
  setSelectedForm,
  aditionalContent,
  allowGuestAccount,
}: RegisterFormProps) {
  const tErrors = useTranslations('Errors.Form');
  const t = useTranslations('Components.Form.RegisterForm');

  const [isLoading, setIsLoading] = useState(false);
  const [showValidateEmailModal, setShowValidateEmailModal] = useState(false);
  const [bypassResetEmailSending, setBypassResetEmailSending] = useState(false);
  const [randomPassword, setRandomPassword] = useState<string>();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [cguAccepted, setCguAccepted] = useState(false);

  const [loginModalDefaultForm, setLoginModalDefaultForm] =
    useState<LoginModalActualForm>('login');

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useForm<Register>({ mode: 'onBlur' });
  const { status } = useSession();

  const promptEmailModal = () => {
    setShowValidateEmailModal(true);
  };

  const handleGenerateRandomPassword = () => {
    const randomGuestPassword = generateRandomPassword();
    setValue('password', randomGuestPassword);
    setRandomPassword(randomGuestPassword);
    copyTextToClipboard(randomGuestPassword);
    toast.success(t('copiedToClipboard'));
  };

  const handleCopyTextToClipboard = () => {
    if (randomPassword) {
      copyTextToClipboard(randomPassword);
      toast.success(t('copiedToClipboard'));
    }
  };

  const onSubmit = () => {
    setShowValidateEmailModal(false);
    setIsLoading(true);

    const formdata = getValues();
    signIn('credentials', {
      id: 'register',
      type: 'register',
      redirect: false,
      email: formdata.email,
      password: formdata.password,
      firstname: formdata.firstname,
      lastname: formdata.lastname,
    })
      .then(async (response) => {
        if (response?.error) {
          const error = JSON.parse(response.error);
          if (error) {
            toast.error(error.message);
          } else {
            toast.error(tErrors('invalid_user_credentials'));
          }
        } else {
          onSuccessCallback?.();
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const passwordWatcher = watch('password');

  return (
    <div className=''>
      <form
        className='mb-10 space-y-4'
        onSubmit={handleSubmit(promptEmailModal)}
      >
        {aditionalContent}
        <div className='space-y-2'>
          <OauthForm />
          <div className='mx-auto  w-full sm:max-w-md'>
            <Button
              variant='basic'
              onClick={() => {
                document.body.scrollIntoView({ behavior: 'smooth' });
                setSelectedForm('login');
              }}
              width='full'
            >
              <span className='font-bold'>{t('alreadyHaveAccount')}</span>
            </Button>
          </div>
        </div>
        <p className='text-lg font-bold text-white-filled-default'>
          Créer un compte
        </p>
        <div className='grid w-full grid-cols-2 gap-2'>
          <TextField
            {...register('firstname', {
              required: true,
            })}
            type='text'
            label={t('firstname')}
            placeholder=''
          />
          <TextField
            {...register('lastname', {
              required: true,
            })}
            type='text'
            label={t('lastname')}
            placeholder=''
          />
        </div>
        <TextField
          {...register('email', {
            required: true,
            pattern: {
              value: EMAIL_REGEX,
              message: tErrors('invalidEmail'),
            },
          })}
          label={t('email')}
          placeholder=''
          error={errors.email}
        />
        <TextField
          {...register('password', {
            required: true,
            pattern: {
              value: PASSWORD_REGEX,
              message: tErrors('invalidPassword'),
            },
          })}
          type='password'
          label={t('password')}
          placeholder=''
          error={errors.password}
        />
        {!randomPassword && (
          <Button
            onClick={handleGenerateRandomPassword}
            variant='link'
            type='button'
          >
            {t('generateRandomPassword')}
          </Button>
        )}
        {randomPassword && (
          <button
            className='ml-3 flex space-x-2'
            onClick={handleCopyTextToClipboard}
            type='button'
          >
            <ClipboardDocumentCheckIcon className='h-5 w-5 font-bold text-primary-filled-default' />
            <p className='font-bold text-primary-filled-default'>
              {t('copyGeneratedPassword')}
            </p>
          </button>
        )}
        <PasswordCriteria password={passwordWatcher} />
        <div className='mb-4 flex items-center'>
          <input
            id='default-checkbox'
            type='checkbox'
            onChange={(e) => {
              setCguAccepted(e.target.checked);
            }}
            value=''
            className='h-5 w-5 rounded-sm border-gray-300 bg-gray-100 text-primary-filled-default focus:ring-2 focus:ring-primary-filled-default '
          />
          <label
            htmlFor='default-checkbox'
            className='ms-2 text-sm font-medium text-white-filled-default'
          >
            {t('iAccept')}{' '}
            <Link
              href='https://out-app.com/terms/terms-of-service-users'
              target='_blank'
              className='text-primary-filled-default underline'
            >
              {t('cgu')}
            </Link>
          </label>
        </div>
        <div className='sm:mx-auto sm:max-w-md'>
          <Button
            id='submit'
            width='full'
            padding='xl'
            type='submit'
            disabled={
              !isValid || isLoading || status === 'loading' || !cguAccepted
            }
            isLoading={isLoading}
          >
            <span className='font-bold'>{t('createAccount')}</span>
          </Button>
        </div>
      </form>
      {showValidateEmailModal && (
        <ValidateEmailModal
          open={showValidateEmailModal}
          setOpen={setShowValidateEmailModal}
          email={getValues('email')}
          onConfirm={onSubmit}
          onCancel={() => setShowValidateEmailModal(false)}
        />
      )}
      {showLoginModal && (
        <LoginModal
          allowGuestAccount={allowGuestAccount}
          open={showLoginModal}
          setOpen={setShowLoginModal}
          email={getValues('email')}
          firstname={getValues('firstname')}
          lastname={getValues('lastname')}
          defaultForm={loginModalDefaultForm}
          onSuccessCallback={onSuccessCallback}
          bypassResetEmailSending={bypassResetEmailSending}
        />
      )}
    </div>
  );
}
